import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, BookOpen, Video, MessageCircle, FileText, Zap, Users, Code, BarChart } from 'lucide-react';
import { Button } from '../components/ui/button';

const HelpCenter = () => {
  const navigate = useNavigate();

  const categories = [
    {
      icon: <Zap className="w-8 h-8" />,
      title: "Getting Started",
      description: "Learn the basics and set up your account in minutes",
      color: "from-blue-500 to-cyan-500",
      articles: [
        "Quick Start Guide",
        "Creating Your First Campaign",
        "Connecting Your Accounts",
        "Dashboard Overview",
        "Account Settings"
      ]
    },
    {
      icon: <BarChart className="w-8 h-8" />,
      title: "SEO Automation",
      description: "Master AI-powered SEO optimization and ranking",
      color: "from-green-500 to-emerald-500",
      articles: [
        "How SEO Automation Works",
        "Keyword Research Guide",
        "On-Page Optimization",
        "Technical SEO Audits",
        "Rank Tracking & Reporting"
      ]
    },
    {
      icon: <Users className="w-8 h-8" />,
      title: "Social Media",
      description: "Automate your social media presence across all platforms",
      color: "from-purple-500 to-pink-500",
      articles: [
        "Social Media Automation 101",
        "Content Calendar Setup",
        "Platform-Specific Strategies",
        "Engagement Analytics",
        "Best Posting Times"
      ]
    },
    {
      icon: <FileText className="w-8 h-8" />,
      title: "Content Creation",
      description: "Generate high-quality content with AI",
      color: "from-orange-500 to-red-500",
      articles: [
        "AI Content Generation",
        "Image Creation (DALL-E 3)",
        "Video Generation (RunwayML)",
        "Blog Writing Tips",
        "Brand Voice Customization"
      ]
    },
    {
      icon: <MessageCircle className="w-8 h-8" />,
      title: "Local SEO & GMB",
      description: "Dominate local search and Google My Business",
      color: "from-indigo-500 to-blue-500",
      articles: [
        "Google My Business Setup",
        "Review Management",
        "Local Citation Building",
        "GMB Post Automation",
        "Local Ranking Factors"
      ]
    },
    {
      icon: <Code className="w-8 h-8" />,
      title: "Integrations & API",
      description: "Connect with your favorite tools and platforms",
      color: "from-yellow-500 to-orange-500",
      articles: [
        "Available Integrations",
        "API Documentation",
        "Webhook Setup",
        "Custom Integrations",
        "WordPress Plugin"
      ]
    }
  ];

  const quickLinks = [
    { icon: <BookOpen className="w-5 h-5" />, title: "Documentation", desc: "Complete guides and references", link: "/docs" },
    { icon: <Video className="w-5 h-5" />, title: "Video Tutorials", desc: "Step-by-step video guides", link: "/tutorials" },
    { icon: <MessageCircle className="w-5 h-5" />, title: "FAQs", desc: "Frequently asked questions", link: "/faq" },
    { icon: <Users className="w-5 h-5" />, title: "Community", desc: "Connect with other users", link: "/community" }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50">
      {/* Navigation */}
      <nav className="navbar px-6 py-4 bg-white/80 backdrop-blur-xl border-b sticky top-0 z-50">
        <div className="container mx-auto flex justify-between items-center">
          <div className="flex items-center space-x-3 cursor-pointer" onClick={() => navigate('/')}>
            <div className="w-11 h-11 bg-gradient-to-br from-blue-600 to-purple-600 rounded-xl flex items-center justify-center text-white font-bold text-xl shadow-lg">A</div>
            <span className="text-2xl font-bold heading bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">AutomateSEO.ai</span>
          </div>
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/faq')}>FAQs</Button>
            <Button variant="ghost" onClick={() => navigate('/')}>
              <ArrowLeft className="mr-2 h-4 w-4" />Back to Home
            </Button>
          </div>
        </div>
      </nav>

      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white py-20">
        <div className="container mx-auto px-6 text-center">
          <h1 className="text-6xl font-bold mb-6">Help Center</h1>
          <p className="text-2xl text-blue-100 mb-8 max-w-3xl mx-auto">
            Everything you need to master AutomateSEO.ai and grow your business
          </p>
          <div className="max-w-2xl mx-auto">
            <input
              type="text"
              placeholder="Search for help articles..."
              className="w-full px-6 py-4 rounded-full text-gray-900 text-lg shadow-2xl focus:outline-none focus:ring-4 focus:ring-blue-300"
            />
          </div>
        </div>
      </div>

      {/* Quick Links */}
      <div className="container mx-auto px-6 -mt-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {quickLinks.map((link, index) => (
            <div
              key={index}
              onClick={() => navigate(link.link)}
              className="bg-white rounded-xl shadow-lg p-6 hover:shadow-2xl transition-all cursor-pointer transform hover:-translate-y-1"
            >
              <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-purple-600 rounded-lg flex items-center justify-center text-white mb-4">
                {link.icon}
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">{link.title}</h3>
              <p className="text-gray-600 text-sm">{link.desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Categories */}
      <div className="container mx-auto px-6 py-16">
        <h2 className="text-4xl font-bold text-center mb-12 text-gray-900">Browse by Category</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {categories.map((category, index) => (
            <div key={index} className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-2xl transition-all transform hover:-translate-y-1">
              <div className={`bg-gradient-to-br ${category.color} p-6 text-white`}>
                <div className="mb-4">{category.icon}</div>
                <h3 className="text-2xl font-bold mb-2">{category.title}</h3>
                <p className="text-white/90">{category.description}</p>
              </div>
              <div className="p-6">
                <ul className="space-y-3">
                  {category.articles.map((article, aIndex) => (
                    <li key={aIndex}>
                      <a href="#" className="text-gray-700 hover:text-blue-600 transition flex items-center gap-2 group">
                        <span className="w-2 h-2 bg-blue-600 rounded-full group-hover:scale-150 transition-transform"></span>
                        {article}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Contact Support */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white py-16">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold mb-4">Need More Help?</h2>
              <p className="text-xl text-blue-100">Our support team is available 24/7 to assist you</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-white/10 backdrop-blur-lg rounded-xl p-6 text-center hover:bg-white/20 transition-all cursor-pointer"
                   onClick={() => window.location.href = 'mailto:support@automateseo.ai'}>
                <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <MessageCircle className="w-8 h-8" />
                </div>
                <h3 className="text-xl font-bold mb-2">Email Support</h3>
                <p className="text-blue-100 mb-3">support@automateseo.ai</p>
                <p className="text-sm text-blue-200">Response within 2 hours</p>
              </div>
              <div className="bg-white/10 backdrop-blur-lg rounded-xl p-6 text-center hover:bg-white/20 transition-all cursor-pointer"
                   onClick={() => window.location.href = 'tel:+919810535398'}>
                <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <MessageCircle className="w-8 h-8" />
                </div>
                <h3 className="text-xl font-bold mb-2">Phone Support</h3>
                <p className="text-blue-100 mb-3">+91 9810535398</p>
                <p className="text-sm text-blue-200">Available 24/7</p>
              </div>
              <div className="bg-white/10 backdrop-blur-lg rounded-xl p-6 text-center hover:bg-white/20 transition-all cursor-pointer"
                   onClick={() => navigate('/faq')}>
                <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <BookOpen className="w-8 h-8" />
                </div>
                <h3 className="text-xl font-bold mb-2">FAQs</h3>
                <p className="text-blue-100 mb-3">Quick Answers</p>
                <p className="text-sm text-blue-200">100+ common questions</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HelpCenter;